import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { alerts, type Alert, type AlertCreate } from "@/api/alerts";
import { ApiError } from "@/api/http";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import { useWsSubscribe } from "@/ws/WebSocketProvider";
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  FormError,
  Input,
  Label,
  Select,
} from "@/components/ui";

export function AlertsPage() {
  const list = useQuery({ queryKey: ["alerts"], queryFn: () => alerts.list(), refetchInterval: 15_000 });
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();

  // Live invalidation when an alert fires.
  useWsSubscribe("price_alert", () => qc.invalidateQueries({ queryKey: ["alerts"] }));

  const cancel = useMutation({
    mutationFn: (id: string) => alerts.cancel(id),
    onSuccess: () => {
      toast.push("Alert canceled", "warn");
      qc.invalidateQueries({ queryKey: ["alerts"] });
    },
    onError: (e) =>
      toast.push(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e), "error"),
  });
  const remove = useMutation({
    mutationFn: (id: string) => alerts.remove(id),
    onSuccess: () => {
      toast.push("Alert deleted", "info");
      qc.invalidateQueries({ queryKey: ["alerts"] });
    },
    onError: (e) =>
      toast.push(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e), "error"),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold text-term-fg">Price alerts</h1>
        <Button onClick={() => setOpen(true)}>New alert</Button>
      </div>

      <Card>
        <CardBody className="p-0">
          {list.isLoading ? (
            <div className="p-3 text-sm text-term-mute">Loading…</div>
          ) : !list.data || list.data.length === 0 ? (
            <EmptyState
              title="No alerts yet"
              description="Watch a price level — armed alerts fire a toast + leave a row here when they cross."
              action={<Button onClick={() => setOpen(true)}>First alert</Button>}
            />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Symbol</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Condition</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Threshold</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Side</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Status</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Fired</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Note</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((a) => (
                  <tr key={a.id} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 font-mono text-term-fg">{a.symbol}</td>
                    <td className="px-3 py-1.5 text-2xs uppercase tracking-wider text-term-dim">
                      {a.condition === "CROSS_ABOVE" ? "↑ above" : "↓ below"}
                    </td>
                    <td className="px-3 py-1.5 text-right tnum text-term-fg">{a.threshold}</td>
                    <td className="px-3 py-1.5 text-2xs text-term-dim">{a.side}</td>
                    <td className="px-3 py-1.5">{statusBadge(a.status)}</td>
                    <td className="px-3 py-1.5 text-2xs text-term-dim">
                      {a.fired_at ? (
                        <>
                          <div>{new Date(a.fired_at).toLocaleString()}</div>
                          <div className="font-mono text-term-mute">@ {a.fired_price}</div>
                        </>
                      ) : "—"}
                    </td>
                    <td className="px-3 py-1.5 text-2xs text-term-dim">{a.note ?? "—"}</td>
                    <td className="px-3 py-1.5 text-right">
                      <div className="flex justify-end gap-1">
                        {a.status === "ARMED" && (
                          <Button variant="ghost" onClick={() => cancel.mutate(a.id)}>
                            Cancel
                          </Button>
                        )}
                        <Button
                          variant="danger"
                          onClick={() => {
                            if (confirm("Delete this alert?")) remove.mutate(a.id);
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {open && <NewAlertModal onClose={() => setOpen(false)} />}
    </div>
  );
}


function statusBadge(s: string) {
  const tone =
    s === "ARMED" ? "good" : s === "FIRED" ? "warn" : "neutral";
  return <Badge tone={tone as "good" | "warn" | "neutral"}>{s}</Badge>;
}


function NewAlertModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<AlertCreate>({
    symbol: "EUR/USD",
    condition: "CROSS_ABOVE",
    side: "MID",
    threshold: "",
    note: "",
  });
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => alerts.create(form),
    onSuccess: () => {
      toast.push("Alert armed", "success");
      qc.invalidateQueries({ queryKey: ["alerts"] });
      onClose();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  return (
    <Modal open onClose={onClose} title="Arm a price alert">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          create.mutate();
        }}
        className="space-y-3"
      >
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Symbol</Label>
            <Input
              value={form.symbol}
              onChange={(e) => setForm({ ...form, symbol: e.target.value.toUpperCase() })}
              placeholder="EUR/USD"
              required
            />
          </div>
          <div>
            <Label>Side</Label>
            <Select
              value={form.side ?? "MID"}
              onChange={(e) => setForm({ ...form, side: e.target.value as "BID" | "ASK" | "MID" })}
            >
              <option value="MID">MID</option>
              <option value="BID">BID</option>
              <option value="ASK">ASK</option>
            </Select>
          </div>
          <div>
            <Label>Condition</Label>
            <Select
              value={form.condition}
              onChange={(e) => setForm({ ...form, condition: e.target.value as "CROSS_ABOVE" | "CROSS_BELOW" })}
            >
              <option value="CROSS_ABOVE">↑ cross above</option>
              <option value="CROSS_BELOW">↓ cross below</option>
            </Select>
          </div>
          <div>
            <Label>Threshold</Label>
            <Input
              type="number"
              step="any"
              value={String(form.threshold)}
              onChange={(e) => setForm({ ...form, threshold: e.target.value })}
              required
            />
          </div>
          <div className="col-span-2">
            <Label>Note (optional)</Label>
            <Input
              value={form.note ?? ""}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="risk-off pair / breakout"
            />
          </div>
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={create.isPending}>Arm alert</Button>
        </div>
      </form>
    </Modal>
  );
}
